// One page of a waste-settings catalog tab (rev-7, UA-waste phase 2).
//
// The categories tab rendered every row at once: 1,081 rows and 2,167 buttons
// on staging, 60,032 px tall. A catalog has two kinds of row, and they page
// differently:
//
//   * the rows the pack or the platform owns (`source` other than `tenant`)
//     are few by construction and are what a reader checks the grid order
//     against, so they are always on screen;
//   * the rows the tenant authored can be any number, so they page, 25 at a
//     time, and a search narrows them by code or name.
//
// A row the reader is working on (being edited, or created in this visit) is
// pinned to the page, so saving a new waste type never sends it to page 12.
// The visible rows keep the catalog's own order, the floor grid's (§4 S8):
// paging decides which rows show, never where they sit.

/** The fields of a catalog row the pager reads. */
export interface PageableCatalogRow {
  readonly code: string;
  readonly source: string;
}

export interface CatalogPageInput<T extends PageableCatalogRow> {
  /** The rows in display order. */
  rows: readonly T[];
  /** 1-based; clamped into range. */
  page: number;
  pageSize?: number | undefined;
  /** Case-insensitive; matches the code or any of `text(row)`. */
  query?: string | undefined;
  text?: ((row: T) => string) | undefined;
  /** Codes always on screen (edited, or created in this visit). */
  pinned?: ReadonlySet<string> | undefined;
}

export interface CatalogPage<T> {
  rows: T[];
  page: number;
  pageCount: number;
  /** 1-based range of the paged rows shown, and how many there are. */
  from: number;
  to: number;
  total: number;
}

/**
 * 25, not the 50 the other office lists page at (D-561). A catalog row is
 * heavy: three flag chips, an owner chip and a row menu, and in Russian at
 * 1440 px the chips wrap, so a row is 55 to 75 px tall. Every M05-S15 run
 * leaves one retired tenant category behind (a code is never deleted), so
 * staging's shard-B tenant reached 44 of them and its categories tab grew
 * to 3,606 px, over the four-screen cap of design-system §3.17. At 50 the
 * first page would have kept growing to 62 rows. At 25 the tab measured
 * 2,626 px in Russian (2.9 screens) with 44 tenant rows and with 124.
 *
 * The owned rows are not paged, so they are the budget left under the cap:
 * the cable pack's 9 and the platform's 3 left 974 px, which 13 more rows
 * at 75 px use up. A pack with 22 or more categories, or a visit that
 * pins that many new rows, can pass the cap again with no change here
 * (D-561 ruling 4).
 */
export const CATALOG_PAGE_SIZE = 25;

/** True for a row the pack or the platform owns. */
export function isOwnedRow(row: PageableCatalogRow): boolean {
  return row.source !== "tenant";
}

export function catalogPage<T extends PageableCatalogRow>(
  input: CatalogPageInput<T>,
): CatalogPage<T> {
  const size = Math.max(1, Math.trunc(input.pageSize ?? CATALOG_PAGE_SIZE));
  const needle = (input.query ?? "").trim().toLowerCase();
  const pinned = input.pinned ?? new Set<string>();
  const matches = (row: T): boolean =>
    needle.length === 0 ||
    row.code.toLowerCase().includes(needle) ||
    (input.text?.(row) ?? "").toLowerCase().includes(needle);

  const candidates = input.rows.filter(matches);
  const paged = candidates.filter((row) => !isOwnedRow(row) && !pinned.has(row.code));
  const pageCount = Math.max(1, Math.ceil(paged.length / size));
  const page = Math.min(Math.max(Math.trunc(input.page), 1), pageCount);
  const start = (page - 1) * size;
  const shown = new Set(paged.slice(start, start + size));
  const rows = candidates.filter(
    (row) => isOwnedRow(row) || pinned.has(row.code) || shown.has(row),
  );
  return {
    rows,
    page,
    pageCount,
    from: paged.length === 0 ? 0 : start + 1,
    to: Math.min(start + size, paged.length),
    total: paged.length,
  };
}
